import { Component, Output, EventEmitter, Input, OnDestroy, ElementRef, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { Subscription } from "rxjs";
import { CdkDragDrop, moveItemInArray, transferArrayItem } from "@angular/cdk/drag-drop";
import { BuilderService } from "../../services/builder.service";
import { StateBuilderService } from "../../services/state-builder.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { EventsService } from "../../../services/events.service";
import { genId } from "../../../core/services/genId";
import RuleTreeBuilder from "../../services/rule.tree";
import { SubHeaderService } from "../../../core/services/sub-header.service";

import { Button, ButtonKind, ButtonPosition, ContextConfig, FlowTemplate, NewCasingPlanConfig, Page, PageType, Question, QuestionKind, Section, SectionConfig, State, StateType, SummaryControlConfig } from "../../../common/models/flow.dto";
import EditCasingPlan = PageType.EditCasingPlan;
import { SubHeader } from "../../../core/models/sub-header.model";

@Component({
    selector: "state-builder-outline",
    templateUrl: "./state-builder-outline.component.html",
    styleUrls: ["./state-builder-outline.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StateBuilderOutlineComponent implements OnDestroy {

  @Input() set currentPage(value: Page) {
    this._currentPage = value;
    if (this.selectedState && this.selectedState.sections) {
      for (let s = 0; s < this.selectedState.sections.length; s++) {
        if (this.selectedState.sections[s].id === this._currentPage.section) {
          this._currentSection = this.selectedState.sections[s];
        }
      }
    }
  }

  get currentSection(): Section {
    if (!this._currentSection && this.selectedState) {
      if (this.selectedState.sections.length === 0) {
        this.selectedState.sections.push({ id: "", name: "", pages: [], isEditing: true } as Section);
      }
      this._currentSection = this.selectedState.sections[0];
    }
    return this._currentSection;
  }

  /* When rules editor is closed using the 'X' in the menu */
  @Input() set rulesEditorClose(value: boolean) {
    if (!value) {
      this.rulesEditorShowing = value;
    }
  }

  get currentPage() {
    if (!this._currentPage) {
      this._currentPage = <Page>{ pageNumber: -1 };;
    }
    return this._currentPage;
  }

  @Input() public selectedState: State;
  @Output() public addRuleEvent: EventEmitter<boolean> = new EventEmitter<boolean>(); /* signal to state builder to close the menu */
  @Output() public editSectionValidationEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public goToDiagram: EventEmitter<string> = new EventEmitter<string>();
  @Output() public setCurrentPage: EventEmitter<Page> = new EventEmitter<Page>();

  public tabMode: string = "row";
  public dragSubscription: Subscription;
  public sectionName: string = "";
  public _currentSection: Section;
  public _currentPage: Page = <Page>{
    pageNumber: -1
  };
  public isRow: boolean = true;
  public isPage: boolean = false;
  public showAddDropdown: boolean = false;
  public showRuleDropdown: boolean = false;
  public rulesEditorShowing: boolean = false;
  public sectionValidationShowing: boolean = false;
  public subHeader: SubHeader = new SubHeader({ title: "Form Builder", buttons: [{ name: "Save", method: () => this.goToWorkflow() }] });
  public deleteSectionDialog: Dialog = new Dialog("Deleting this section will delete all pages in the section. Do you want to continue?", [new DialogButton("Yes", true, () => this.onDeleteSectionConfirm()), new DialogButton("No", false, () =>  this.onDialogClose())]);
  public deletePageDialog: Dialog = new Dialog("Are you sure you want to delete this page?", [new DialogButton("Yes", true, () => this.onDeletePageConfirm()), new DialogButton("No", false, () =>  this.onDialogClose())]);
  public pageTitlePlaceholder: string = 'Enter Page Title';
  public sectionIndex: number;
  public selectedSection: Section;
  public pageIndex: number;

  private _questionsChangedSubscription: Subscription | undefined;

  constructor(
    public _subHeaderService: SubHeaderService,
    public _eventsService: EventsService,
    public dialogService: DynamicDialogService,
    public el: ElementRef,
    public builder: BuilderService,
    public _changeDetector: ChangeDetectorRef,
    public stateBuilderService: StateBuilderService
  ) {

  }
  ngOnInit(): void {
    //adds what Form you're editing to SubHeader
    if (this.selectedState && this.selectedState.name)
      this.subHeader.title += ": " + this.selectedState.name;

      this._subHeaderService.updateSubHeader(this.subHeader);

    this._questionsChangedSubscription = this.stateBuilderService.questionsChanged$.subscribe(() => {
      this._changeDetector.markForCheck();
    });
  }

  ngOnDestroy() {
    if (this.dragSubscription) {
      this.dragSubscription.unsubscribe();
    }
     this._questionsChangedSubscription?.unsubscribe();
  }

  public toggleDropDown(): void {
    this.showAddDropdown = !this.showAddDropdown;
    if (this.showAddDropdown)
      this._eventsService.create("outline", window, "click", () => this.toggleDropDown());
    else
      this._eventsService.remove("outline");
  }

  public toggleRuleDropDown(): void {
    this.showRuleDropdown = !this.showRuleDropdown;
    if (this.showRuleDropdown)
      this._eventsService.create("outline", window, "click", () => this.toggleRuleDropDown());
    else
      this._eventsService.remove("outline");
  }

  /**
   * Hide/shows section content
   * @param i - section number, used to find section "id"
   * @param e - event to stop bubbling
   */
  public toggleSectionPages(i: number, e: Event): void {
    //stop bubble to title click event
    e.stopPropagation();

    let targetSection = <HTMLDivElement>this.el.nativeElement.querySelector("#section" + i);
    let arrow = <HTMLSpanElement>e.currentTarget;

    //switch arrows
    if (arrow) {
      //hide clicked arrow
      arrow.classList.remove("display-inline-block");
      let arrowOther;
      //get other arrow
      if (arrow.classList.contains("arrow-up")) {
        arrowOther = <HTMLSpanElement>arrow.parentElement.querySelector(".arrow-dwn");
      } else {
        arrowOther = <HTMLSpanElement>arrow.parentElement.querySelector(".arrow-up");

      }
      //show other arrow
      arrowOther.classList.add("display-inline-block");
    }
    //hide/show section pages
    if (targetSection) {
      let targetPages = targetSection.querySelectorAll(".sb-outline-page-row-container");
      for (let i = 0; i < targetPages.length; i++) {
        let targetPage = <HTMLDivElement>targetPages[i];
        if (targetPage) {
          if (targetPage.classList.contains("display-block")) {
            targetPage.classList.remove("display-block");
          } else {
            targetPage.classList.add("display-block");
          }
        }
      }
    }
  }

  /**
   * When tabbing
   * @param event - focusout event
   */
  public closeButtonMenu(event: FocusEvent): void {
    let targetButton = <HTMLButtonElement>event.relatedTarget;
    if (!targetButton) {
      this.showAddDropdown = false;
    } else {
      let buttonMenuContainer = targetButton.closest(".button-menu-options-container");
      if (!buttonMenuContainer) {
        this.showAddDropdown = false;
      }
    }
  }

  public addLeftButton(buttonLabel: string, disableTypeChange: boolean): Button {
    return {
      id: genId(),
      position: ButtonPosition.Left,
      label: buttonLabel,
      type: ButtonKind.Back,
      visibleRule: null,
      enabledRule: null,
      targetPageRule: null,
      targetTransitionRule: null,
      disableTypeChange: disableTypeChange
    } as Button;
  }

  public addRightButton(buttonLabel: string, type: ButtonKind, disableTypeChange: boolean): Button {
    return {
      id: genId(),
      position: ButtonPosition.Right,
      label: buttonLabel,
      type: type,
      visibleRule: null,
      enabledRule: null,
      targetPageRule: null,
      targetTransitionRule: null,
      disableTypeChange: disableTypeChange
    } as Button;
  }

  public addNewFormPage(type: PageType): void {
    const newPage = this.getNewPage(this.currentSection, this.getTotalPages() + 1, type);
    this.currentSection.pages.push(newPage);
  }

  public copyContext(contextTarget: string): void {
    //creates temp textarea for copy to clipboard
    let txtArea = document.createElement("textarea");
    txtArea.style.position = "fixed";
    txtArea.style.left = "0";
    txtArea.style.top = "0";
    txtArea.style.opacity = "0";
    txtArea.value = contextTarget;
    document.body.appendChild(txtArea);
    txtArea.focus();
    txtArea.select();
    //copies to clipboard
    document.execCommand("copy");
    //remove temp textarea
    document.body.removeChild(txtArea);
  }

  public deletePage(section: Section, page: Page): void {
    event.stopPropagation();
    this.selectedSection = section;
    this.pageIndex = this.selectedSection.pages.findIndex(x => x.pageNumber === page.pageNumber);
    if (this.selectedState && (this.pageIndex !== -1)) {
      this.deletePageDialog.toggleDialog(true);
      this.dialogService.updateDialog(this.deletePageDialog);
    }
  }

  private onDeletePageConfirm(): void {
    this.selectedSection.pages.splice(this.pageIndex, 1);
    this.renumberPages();
    this._changeDetector.detectChanges();
  };

  public getNewPage(section: Section, pageNumber: number, pageType: PageType): Page {
    let title = "";
    switch (pageType) {
      case PageType.Summary:
        title = "Summary";
        break;
      case PageType.NewCasingPlan:
        title = "Proposed Casing Plan";
        break;
      case PageType.EditCasingPlan:
        title = "Edit Casing Plan";
        break;
      default:
        title = "Enter Page Title";
        break;
    };

    const newPage: Page = {
      pageNumber: pageNumber,
      pageType: pageType,
      subtitle: title,
      questions: [],
      section: section.id,
      buttons: new Array<Button>(),
      incomingRules: [],
      isSectionHead: false
    };

    // Pre-fill the necessary question-types since the summary editor won't be draggable
    if (pageType === PageType.Summary && this.selectedState) {
      newPage.questions = this.prefillSummaryQuestions(this.selectedState.stateType);
    } else if (pageType === PageType.NewCasingPlan) {
      newPage.questions = this.prefillNewCasingPlanQuestions();
    } else if (pageType === PageType.EditCasingPlan) {
      newPage.questions = this.prefillEditCasingPlanQuestions();
    }

    if (pageType === PageType.NewCasingPlan) {
      newPage.buttons.push(this.addLeftButton("Previous", false));
      newPage.buttons.push(this.addRightButton("Next", ButtonKind.Next, false));
    }

    if (pageType === PageType.Input) {

      if (newPage.pageNumber !== 1) {
        newPage.buttons.push(this.addLeftButton("Previous", false));
      };

      newPage.buttons.push(this.addRightButton("Next", ButtonKind.Next, false));

    } else if (pageType === PageType.Summary && this.selectedState) {

      if (this.selectedState.stateType === StateType.InputForm) {
        newPage.buttons.push(this.addRightButton("Submit", ButtonKind.Transition, false));
      } else if (this.selectedState.stateType === StateType.ReviewForm) {
        newPage.buttons.push(this.addRightButton("Next", ButtonKind.Next, false));
        newPage.buttons.push(this.addRightButton("Approve", ButtonKind.Approve, true));
      };

    };

    return newPage;
  }

  public goToWorkflow(): void {
    this.goToDiagram.emit();
  }

  public prefillEditCasingPlanQuestions(): Array<Array<Question>> {
    const result = new Array<Array<Question>>();
    const summaryRow = new Array<Question>();
    const summary = {
      key: genId(),
      label: "Form Actions",
      type: QuestionKind.EditCasingPlan,
      defaultValue: "",
      //files: [],
      validationRules: [],
      changeRules: [],
      notes: []
    } as Question;
    summaryRow.push(summary);
    result.push(summaryRow);
    return result;
  }

  public prefillNewCasingPlanQuestions(): Array<Array<Question>> {
    const result = new Array<Array<Question>>();
    const summaryRow = new Array<Question>();
    const summary = {
      key: genId(),
      label: "Casing Plan",
      type: QuestionKind.NewCasingPlan,
      defaultValue: "",
      //files: [],
      validationRules: [],
      changeRules: [],
      notes: []
    } as Question;

    if (this.selectedState !== undefined &&
      this.selectedState !== null &&
      this.selectedState.stateType === StateType.ReviewForm) {
      summary.config = {
        isReviewForm: true,
        pages: new Array<Page>(),
        contextValues: new Array<ContextConfig>(),
        configurations: new Array<SectionConfig>()
      } as NewCasingPlanConfig;
    };

    summaryRow.push(summary);
    result.push(summaryRow);
    return result;
	}

	onSectionDrop(event: CdkDragDrop<Section[]>) {
		moveItemInArray(this.selectedState.sections, event.previousIndex, event.currentIndex);
		this._changeDetector.detectChanges();
	}

	onPageDrop(event: CdkDragDrop<Page[]>, section: Section) {
		if (event.previousContainer === event.container) {
			moveItemInArray(section.pages, event.previousIndex, event.currentIndex);
		} else if (event.previousContainer.data && event.container.data) {
			transferArrayItem(
				event.previousContainer.data,
				event.container.data,
				event.previousIndex,
				event.currentIndex
			);
		}
		this.renumberPages();
		this._changeDetector.detectChanges();
	}

	onQuestionRowDrop(event: CdkDragDrop<any[]>, page: Page) {
		moveItemInArray(page.questions, event.previousIndex, event.currentIndex);
		this._changeDetector.detectChanges();
    this.stateBuilderService.questionsChanged$.next();
	}

  public prefillSummaryQuestions(stateType: StateType): Array<Array<Question>> {

    const result = new Array<Array<Question>>();
    const labelRow = new Array<Question>();
    const labelQuestion = {
      key: genId(),
      label: "Please review all information for accuracy.",
      type: QuestionKind.Label,
      defaultValue: "",
      //files: [],
      validationRules: [],
      changeRules: [],
      notes: []
    } as Question;

    if (stateType === StateType.ReviewForm) {
      labelQuestion.label =
        `${labelQuestion.label} <br /> The \"add comments\" links are displayed to the permit requestor, to add internal supervisor comments click the \"supervisor\" comments button above.`;
    };

    labelRow.push(labelQuestion);

    const summaryRow = new Array<Question>();
    const summary = {
      key: genId(),
      label: "Form Summary",
      type: QuestionKind.Summary,
      defaultValue: "",
      //files: [],
      validationRules: [],
      changeRules: [],
      notes: []
    } as Question;

    if (this.selectedState !== undefined &&
      this.selectedState !== null &&
      this.selectedState.stateType === StateType.ReviewForm) {
      summary.config = {
        isReviewForm: true,
        sections: new Array<Section>(),
        contextValues: new Array<ContextConfig>(),
        configurations: new Array<SectionConfig>()
      } as SummaryControlConfig;
    };

    summaryRow.push(summary);

    const commentsRow = this.builder.generateReviewComments();

    result.push(labelRow);
    result.push(summaryRow);
    result.push(commentsRow);

    return result;
  }

  //public setupDragula(): void {
  //  if (this._dragulaService.find("selectedState.sections"))
  //    return;

  //  this._dragulaService.createGroup("selectedState.sections", {
  //    removeOnSpill: false,
  //    moves: function (el, container, target) {
  //      return target.closest(".handle") !== null;
  //    }
  //  });

  //  this._dragulaService.createGroup("page.questions", {
  //    removeOnSpill: false,
  //    moves: function (el, container, target) {
  //      return target.closest(".handle") !== null;
  //    }
  //  });

  //  this.dragSubscription = this._dragulaService.drop("selectedState.sections").subscribe(() => {
  //    if (this.selectedState) {
  //      for (let s = 0; s < this.selectedState.sections.length; s++) {
  //        const section = this.selectedState.sections[s];
  //        if (!section.pages) {
  //          section.pages = [];
  //        }
  //        if (section.pages.length === 0) {
  //          const page = this.getNewPage(section, 0, PageType.Input); //Pages will get renumbered anyway, so number here doesn't matter
  //          section.pages.push(page);
  //        }
  //      }
  //      this.renumberPages();
  //    }
  //  });
  //}

  public setActivePage(page: Page): void {
    this.setCurrentPage.emit(page);
  }

  public setMode(mode: string): void {
    this.tabMode = mode;
  }

  public showRuleEditor(): void {
    this.rulesEditorShowing = !this.rulesEditorShowing;
    this.addRuleEvent.emit(this.rulesEditorShowing);
  }

  public getSectionName(section: Section): string {
    return (section.name) ? section.name : "Enter Section Title";
  }

  public editSectionName(section: Section, event: MouseEvent): void {
    //prevent bubble to setActivateSection()
    event.stopPropagation();
    this.sectionName = section.name;
    section.isEditing = true;
    if (this.selectedState) {
      for (let s = 0; s < this.selectedState.sections.length; s++) {
        if (this.selectedState.sections[s].name !== section.name) {
          this.selectedState.sections[s].isEditing = false;
        }
      }
    }
  }

  public saveSectionName(section: Section): void {
    section.name = this.sectionName;
    this.sectionName = "";
    section.isEditing = false;
  }

  public deleteSection(section: Section, event: MouseEvent): void {
    //prevent bubble to setActivateSection()
    event.stopPropagation();
    if (this.selectedState) {
      this.sectionIndex = this.selectedState.sections.findIndex(s => s.id === section.id);
    }
    if (this.sectionIndex !== -1) {
        this.deleteSectionDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.deleteSectionDialog);
    } else {
      console.error("unable to determine section index");
    }
  }

  private onDeleteSectionConfirm(): void {
    if (this.selectedState) {
      this.selectedState.sections.splice(this.sectionIndex, 1);
      if (this.selectedState.sections.length === 0) {
        this.addSection();
        this.setActivePage(this.selectedState.sections[0].pages[0]);
      } else {
        this.sectionIndex = (this.sectionIndex < this.selectedState.sections.length) ? this.sectionIndex : this.selectedState.sections.length - 1;
        this.setActivePage(this.selectedState.sections[this.sectionIndex].pages[0]);
      }
      this.renumberPages();
    }
  }

  public setActiveSection(section: Section): void {
    if (section.pages.length === 0) return;
    this.setActivePage(section.pages[0]);
  }

  public addSection(): void {
    const newSection = { id: genId(), name: "", pages: [] } as Section;
    const page = this.getNewPage(newSection, this.getTotalPages() + 1, PageType.Input);
    newSection.pages.push(page);
    if (this.selectedState) {
      this.selectedState.sections.push(newSection);
    }
    this.setActivePage(newSection.pages[0]);
  }

  public editSectionValidation(): void {
    this.sectionValidationShowing = !this.sectionValidationShowing;
    this.editSectionValidationEvent.emit(this.sectionValidationShowing);
  }

  public getTotalPages(): number {
    let totalPages = 0;
    if (this.selectedState) {
      for (let s = 0; s < this.selectedState.sections.length; s++) {
        totalPages = totalPages + this.selectedState.sections[s].pages.length;
      }
    }
    return totalPages;
  }

  public isCurrentPage(page: Page): boolean {
    if (!this.currentPage) return false;
    return page.pageNumber === this.currentPage.pageNumber;
  }

  public renumberPages(): void {
    let renumber = 0;
    if (this.selectedState) {
      for (let s = 0; s < this.selectedState.sections.length; s++) {
        for (let p = 0; p < this.selectedState.sections[s].pages.length; p++) {
          renumber = renumber + 1;
          this.selectedState.sections[s].pages[p].pageNumber = renumber;
        }
      }
    }
  }

  public onDialogClose(): void {

  }

  public sectionIsActive(id: string): boolean {
      if (this._currentSection) {
          return id === this._currentSection.id;
      }
      const section = this.selectedState.sections.findIndex(s => s.id === id);
      return section === 0;
  }

}
